import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SavingsIcon from "@mui/icons-material/Savings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import { fetchSubscriptionRequest } from "../../../redux/sagas/Subscription/SubscriptionSlice";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { canShowPermission } from "../Permissions/permissionObject";

const statusColor = (status) => {
  const value = String(status || "").toUpperCase();
  if (["ACTIVE", "SUCCESS", "PAID", "COMPLETED", "RUNNING"].includes(value)) {
    return "success";
  }
  if (["PENDING", "PAUSE"].includes(value)) return "warning";
  if (["CANCEL", "CANCELLED", "FAILED", "EXPIRED"].includes(value)) {
    return "error";
  }
  return "default";
};

const formatDateTime = (value) => {
  if (!value) return "N.A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N.A";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const addCycles = (start, cycles, intervalType) => {
  const date = new Date(start);
  if (Number.isNaN(date.getTime())) return null;
  const count = Number(cycles) || 0;
  switch (String(intervalType || "MONTH").toUpperCase()) {
    case "DAY":
      date.setDate(date.getDate() + count);
      break;
    case "WEEK":
      date.setDate(date.getDate() + count * 7);
      break;
    case "YEAR":
      date.setFullYear(date.getFullYear() + count);
      break;
    case "MONTH":
    default:
      date.setMonth(date.getMonth() + count);
      break;
  }
  return date;
};

const getSubscriptionExpiry = (subscription) => {
  if (!subscription) return null;
  if (subscription.expiryAt) return subscription.expiryAt;
  if (subscription.sip?.startDate && subscription.sip?.noOfMonths) {
    return addCycles(
      subscription.sip.startDate,
      subscription.sip.noOfMonths,
      subscription.interval || subscription.sipPlan?.interval
    );
  }
  return subscription.subscription_expiry_time;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "N.A";
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;
  return `₹${amount.toLocaleString("en-IN")}`;
};

const formatDate = (value) => {
  if (!value) return "N.A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N.A";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const intervalLabel = (interval) => {
  switch (String(interval || "").toUpperCase()) {
    case "DAY":
      return "Every day";
    case "WEEK":
      return "Every week";
    case "YEAR":
      return "Every year";
    case "MONTH":
      return "Every month";
    default:
      return interval || "N.A";
  }
};

const customerName = (customer) =>
  customer
    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "N.A"
    : "N.A";

const readRefId = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "object") {
    if (typeof value.toHexString === "function") return value.toHexString();
    if (value._id) return readRefId(value._id);
  }
  return String(value);
};

const sameRef = (left, right) => {
  const a = readRefId(left);
  const b = readRefId(right);
  return Boolean(a) && a === b;
};

const scopeSubscriptionRow = (row) => {
  if (!row) return row;
  const jewellerId = readRefId(row.jeweller);
  const customerId = readRefId(row.customer);
  const sipBelongs =
    row.sip &&
    sameRef(row.sip.jeweller, jewellerId) &&
    sameRef(row.sip.customer, customerId);
  const sipId = sipBelongs ? readRefId(row.sip) : "";
  const sipInstallments = sipBelongs
    ? (row.sipInstallments || []).filter(
        (installment) =>
          sameRef(installment?.jeweller, jewellerId) &&
          sameRef(installment?.customer, customerId) &&
          sameRef(installment?.sip, sipId)
      )
    : [];
  const sipPlan =
    sipBelongs &&
    row.sipPlan &&
    (!readRefId(row.sipPlan.jeweller) ||
      sameRef(row.sipPlan.jeweller, jewellerId))
      ? row.sipPlan
      : null;

  return {
    ...row,
    sip: sipBelongs ? row.sip : null,
    sipPlan,
    sipInstallments,
    paidInstallments: sipInstallments.filter(
      (installment) => String(installment?.status || "").toUpperCase() === "PAID"
    ).length,
    pendingInstallments: sipInstallments.filter(
      (installment) =>
        String(installment?.status || "").toUpperCase() === "PENDING"
    ).length,
  };
};

const mandateInfo = (subscription) => {
  const cancelled =
    subscription?.mandateCancelled || subscription?.status === "Cancel";
  if (cancelled) {
    return {
      label: "Auto-debit stopped",
      color: "error",
      help: "The customer cancelled the UPI mandate. Recurring bank debit has stopped. The SIP can still be paid manually.",
    };
  }
  switch (subscription?.status) {
    case "Active":
      return {
        label: "Auto-debit active",
        color: "success",
        help: "The UPI mandate is active. The bank will deduct the installment amount on schedule.",
      };
    case "Pending":
      return {
        label: "Waiting for approval",
        color: "warning",
        help: "The customer has not finished approving the UPI mandate yet.",
      };
    case "Pause":
      return {
        label: "Auto-debit paused",
        color: "warning",
        help: "Auto-debit is paused. No new bank deductions will happen until it is resumed.",
      };
    case "Expired":
      return {
        label: "Auto-debit ended",
        color: "default",
        help: "This auto-debit plan has reached its end date.",
      };
    default:
      return {
        label: subscription?.status || "Unknown",
        color: statusColor(subscription?.status),
        help: "Current status of this UPI auto-debit.",
      };
  }
};

const chargeStatusLabel = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "SUCCESS":
      return "Money deducted";
    case "PENDING":
      return "Waiting";
    case "FAILED":
      return "Failed";
    default:
      return status || "N.A";
  }
};

const SummaryCard = ({ icon, label, value, hint }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: "100%",
      borderRadius: "14px",
      border: "1px solid #EEE8FF",
      background: "linear-gradient(180deg, #FBF9FF 0%, #FFFFFF 100%)",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          backgroundColor: "#EFE7FF",
          color: "#7A3FFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "#6B6780", fontWeight: 600, letterSpacing: 0.3 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 16,
            color: "#2B2145",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: "#8A869C" }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const SectionCard = ({ title, subtitle, icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 2.5,
      borderRadius: "16px",
      border: "1px solid #EDEAF5",
      overflow: "hidden",
      maxWidth: "100%",
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        backgroundColor: "#FAF8FF",
        borderBottom: "1px solid #EDEAF5",
      }}
    >
      <Box sx={{ color: "#7A3FFF", display: "flex" }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 700, color: "#2B2145" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "#6B6780" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ p: 2.5, overflowX: "auto", maxWidth: "100%" }}>{children}</Box>
  </Paper>
);

const tableHeadSx = {
  "& th": {
    backgroundColor: "#F4F0FF",
    color: "#4A3A78",
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
};

const AllSubscriptionList = () => {
  const dispatch = useDispatch();
  const subscriptionState = useSelector(
    (state) => state.subscriptionPlan || {}
  );
  const { allSubscriptionList = [], total = 0, loading } = subscriptionState;

  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);

  const canCancelMandate = canShowPermission(
    permissions,
    "allSubscriptionList",
    "edit"
  );

  useEffect(() => {
    dispatch(
      fetchSubscriptionRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
        status: statusFilter,
      })
    );
  }, [dispatch, pagination, search, statusFilter]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;

      case "changeRowsPerPage":
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;

      case "search":
        setSearch(tableState.searchText || "");
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;

      default:
        break;
    }
  };

  const canCancelRow = (row) =>
    canCancelMandate &&
    ["Active", "Pending", "Pause"].includes(row?.status);

  const handleCancelConfirm = () => {
    if (!cancelTarget?._id) return;
    setCancelling(true);
    dispatch(
      apiRequest({
        method: "POST",
        endpoint: "/api/v1/transactions/manageCfSubscription",
        body: { subscriptionId: cancelTarget._id, action: "CANCEL" },
        callback: (response) => {
          setCancelling(false);
          const errorMessage =
            response?.errormsg ||
            (typeof response?.message === "string"
              ? response.message
              : response?.message?.message);
          if (response?.error || !response?.success) {
            toast.error(
              errorMessage || "Failed to cancel UPI mandate."
            );
            return;
          }
          toast.success(
            errorMessage ||
              "UPI mandate cancelled. Recurring auto-debit has been stopped."
          );
          setCancelTarget(null);
          setSelectedSubscription(null);
          dispatch(
            fetchSubscriptionRequest({
              start: pagination.page * pagination.limit,
              limit: pagination.limit,
              search,
              status: statusFilter,
            })
          );
        },
      })
    );
  };

  const columns = [
    {
      field: "customer",
      headerName: "Name",
      minWidth: 160,
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.lastName || ""}`.trim()
          : "N.A",
    },
    {
      field: "customer.phone",
      headerName: "Phone",
      minWidth: 130,
      render: (val, row) => row.customer?.phone || "N.A",
    },
    {
      field: "amount",
      headerName: "Amount",
      minWidth: 110,
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 100,
    },
    {
      field: "interval",
      headerName: "Interval",
      minWidth: 110,
    },
    {
      field: "sipPlan",
      headerName: "SIP Plan",
      minWidth: 160,
      render: (val, row) => row.sipPlan?.name || "N.A",
    },
    {
      field: "sip.status",
      headerName: "SIP Status",
      minWidth: 130,
      render: (val, row) =>
        row.sip?.status ? (
          <Chip
            size="small"
            label={row.sip.status}
            color={statusColor(row.sip.status)}
          />
        ) : (
          "N.A"
        ),
    },
    {
      field: "paidInstallments",
      headerName: "SIP Paid / Total",
      minWidth: 140,
      render: (val, row) =>
        row.sip
          ? `${row.paidInstallments ?? 0} / ${row.sip.noOfMonths ?? 0}`
          : "N.A",
    },
    {
      field: "lastCharge",
      headerName: "Charge Created",
      minWidth: 170,
      render: (val, row) => formatDateTime(row.lastCharge?.createdAt),
    },
    {
      field: "lastDebitedCharge",
      headerName: "Last Debited",
      minWidth: 170,
      render: (val, row) =>
        formatDateTime(
          row.lastDebitedCharge?.updatedAt || row.lastDebitedCharge?.createdAt
        ),
    },
    {
      field: "lastFailureReason",
      headerName: "Why last debit failed",
      minWidth: 220,
      render: (val, row) => {
        const reason =
          row.lastFailureReason ||
          row.lastFailedCharge?.failureReason ||
          row.mandateFailureReason;
        if (!reason) return "—";
        return (
          <Tooltip title={reason}>
            <Chip
              size="small"
              color="error"
              label={reason}
              sx={{
                maxWidth: 200,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "mandateCancelled",
      headerName: "UPI Mandate",
      minWidth: 170,
      render: (val, row) => (
        <Chip
          size="small"
          label={
            row.mandateCancelled || row.status === "Cancel"
              ? "Cancelled"
              : row.status || "N.A"
          }
          color={
            row.mandateCancelled || row.status === "Cancel"
              ? "error"
              : statusColor(row.status)
          }
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      render: (val) => (
        <Chip size="small" label={val || "N.A"} color={statusColor(val)} />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 210,
      sortable: false,
      hideInExcel: true,
      render: (val, row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            sx={{ backgroundColor: "#7A3FFF", textTransform: "none" }}
            onClick={() => setSelectedSubscription(row)}
          >
            Details
          </Button>
          {canCancelRow(row) && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              sx={{ textTransform: "none" }}
              onClick={() => setCancelTarget(row)}
            >
              Cancel
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/subscription/allSubscriptionCsv",
        query: { status: statusFilter },
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "allSubscriptionCsv.csv");
        },
      })
    );
  };

  const sortedCharges = [...(selectedSubscription?.charges || [])].sort(
    (a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0)
  );
  const linkedSipId = readRefId(selectedSubscription?.sip);
  const subscriptionJewellerId = readRefId(selectedSubscription?.jeweller);
  const subscriptionCustomerId = readRefId(selectedSubscription?.customer);
  const sortedInstallments = [
    ...(selectedSubscription?.sipInstallments || []),
  ]
    .filter(
      (installment) =>
        sameRef(installment?.sip, linkedSipId) &&
        sameRef(installment?.jeweller, subscriptionJewellerId) &&
        sameRef(installment?.customer, subscriptionCustomerId)
    )
    .sort(
      (a, b) =>
        Number(a?.installmentDate || 0) - Number(b?.installmentDate || 0)
    );
  const mandateStillPending =
    selectedSubscription?.status === "Pending" ||
    String(selectedSubscription?.sip?.status || "").toUpperCase() ===
      "PENDING";
  const paidInstallmentCount = selectedSubscription?.paidInstallments ?? 0;
  const totalInstallmentCount = Number(
    selectedSubscription?.sip?.noOfMonths || 0
  );
  const remainingInstallmentCount = totalInstallmentCount
    ? Math.max(totalInstallmentCount - paidInstallmentCount, 0)
    : 0;

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          All Subscriptions
        </Typography>

        <CardContent sx={{ height: "100%", width: "100%" }}>
          <TableCompo
            columns={columns}
            data={JSON.parse(
              JSON.stringify((allSubscriptionList || []).map(scopeSubscriptionRow))
            )}
            totalCount={total}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            loading={loading}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadStoreCsv}
              >
                Download CSV
              </Button>
            }
            toolbarExtra={
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {[
                  { value: "Active", label: "Active", color: "#4CAF50" },
                  { value: "Pending", label: "Pending", color: "#2196F3" },
                  { value: "Cancel", label: "Cancelled", color: "#f44336" },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setStatusFilter(option.value);
                      setPagination((prev) =>
                        prev.page === 0 ? prev : { ...prev, page: 0 }
                      );
                    }}
                    sx={{
                      backgroundColor:
                        statusFilter === option.value
                          ? option.color
                          : "#E0E0E0",
                      color:
                        statusFilter === option.value ? "white" : "black",
                      textTransform: "none",
                      height: 32,
                      fontSize: 12,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor:
                          statusFilter === option.value
                            ? option.color
                            : "#D5D5D5",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
            }
          />
        </CardContent>
      </Box>

      <Dialog
        open={Boolean(selectedSubscription)}
        onClose={() => setSelectedSubscription(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "18px", overflow: "hidden" },
        }}
      >
        {selectedSubscription && (
          <>
            <Box
              sx={{
                px: 3,
                py: 2.5,
                background: "linear-gradient(135deg, #7A3FFF 0%, #5B2BD6 100%)",
                color: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 13, opacity: 0.85, mb: 0.5 }}>
                  Auto-debit subscription
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 24, lineHeight: 1.2 }}>
                  {customerName(selectedSubscription.customer)}
                </Typography>
                <Typography sx={{ mt: 0.5, opacity: 0.92 }}>
                  {selectedSubscription.customer?.phone || "No phone"}
                  {selectedSubscription.customer?.email
                    ? `  ·  ${selectedSubscription.customer.email}`
                    : ""}
                </Typography>
                <Chip
                  size="small"
                  label={mandateInfo(selectedSubscription).label}
                  sx={{
                    mt: 1.25,
                    fontWeight: 700,
                    backgroundColor: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.35)",
                  }}
                />
              </Box>
              <IconButton
                onClick={() => setSelectedSubscription(null)}
                sx={{ color: "#fff" }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <DialogContent
              sx={{
                p: 3,
                backgroundColor: "#F7F5FC",
                overflowX: "hidden",
                minWidth: 0,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: "14px",
                  border: "1px solid #E6E0F8",
                  backgroundColor: "#fff",
                  display: "flex",
                  gap: 1.25,
                  alignItems: "flex-start",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#7A3FFF", mt: "2px" }} />
                <Typography sx={{ color: "#4A4460", fontSize: 14 }}>
                  {mandateInfo(selectedSubscription).help}
                </Typography>
              </Paper>

              {(selectedSubscription.lastFailureReason ||
                selectedSubscription.lastFailedCharge?.failureReason ||
                selectedSubscription.mandateFailureReason) && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: "14px",
                    border: "1px solid #F5C2C7",
                    backgroundColor: "#FFF5F5",
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: "#D32F2F", mt: "2px" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#B42318" }}>
                      Last debit failed
                    </Typography>
                    <Typography sx={{ color: "#7A1F1F", fontSize: 14 }}>
                      {selectedSubscription.lastFailureReason ||
                        selectedSubscription.lastFailedCharge?.failureReason ||
                        selectedSubscription.mandateFailureReason}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#9B4A4A", display: "block", mt: 0.5 }}
                    >
                      This message comes from the payment gateway (Cashfree).
                    </Typography>
                  </Box>
                </Paper>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <SummaryCard
                  icon={<AccountBalanceWalletIcon fontSize="small" />}
                  label="Amount deducted"
                  value={formatAmount(
                    selectedSubscription.sip?.installmentAmount ||
                      selectedSubscription.amount
                  )}
                  hint="Money taken from the customer's bank"
                />
                <SummaryCard
                  icon={<EventRepeatIcon fontSize="small" />}
                  label="How often"
                  value={intervalLabel(selectedSubscription.interval)}
                  hint={selectedSubscription.type || "Auto-debit"}
                />
                <SummaryCard
                  icon={<EventAvailableIcon fontSize="small" />}
                  label="Ends on"
                  value={formatDate(getSubscriptionExpiry(selectedSubscription))}
                  hint="Calculated from SIP duration and interval"
                />
                <SummaryCard
                  icon={<ReceiptLongIcon fontSize="small" />}
                  label="Last money deducted"
                  value={formatDate(
                    selectedSubscription.lastDebitedCharge?.updatedAt ||
                      selectedSubscription.lastDebitedCharge?.createdAt
                  )}
                  hint={
                    selectedSubscription.lastCharge?.createdAt
                      ? `Last request: ${formatDate(
                          selectedSubscription.lastCharge.createdAt
                        )}`
                      : "No debit yet"
                  }
                />
              </Box>

              <SectionCard
                icon={<SavingsIcon />}
                title="Linked SIP"
                subtitle="The savings plan this auto-debit is paying for"
              >
                {selectedSubscription.sip ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                        gap: 2,
                        mb: 2.5,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: "#6B6780" }}>
                          Plan name
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {selectedSubscription.sipPlan?.name || "N.A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#6B6780" }}>
                          Metal
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {selectedSubscription.sip.metalName || "Money"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#6B6780" }}>
                          SIP started
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {formatDate(selectedSubscription.sip.startDate)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: "#2B2145" }}>
                        SIP progress
                      </Typography>
                      <Chip
                        size="small"
                        label={selectedSubscription.sip.status || "N.A"}
                        color={statusColor(selectedSubscription.sip.status)}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        totalInstallmentCount
                          ? (paidInstallmentCount / totalInstallmentCount) * 100
                          : 0
                      )}
                      sx={{
                        height: 10,
                        borderRadius: 99,
                        backgroundColor: "#EDE7FF",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#7A3FFF",
                          borderRadius: 99,
                        },
                      }}
                    />
                    <Typography sx={{ mt: 1, color: "#5B5670" }}>
                      {paidInstallmentCount} paid · {remainingInstallmentCount}{" "}
                      remaining
                      {totalInstallmentCount
                        ? ` · ${totalInstallmentCount} total installments`
                        : ""}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ color: "#6B6780" }}>
                    This auto-debit is not linked to a SIP. It is a standalone
                    buy mandate.
                  </Typography>
                )}
              </SectionCard>

              {selectedSubscription.sip && (
                <SectionCard
                  icon={<PersonOutlineIcon />}
                  title="SIP installments"
                  subtitle="Each installment of the customer's savings plan"
                >
                  {sortedInstallments.length > 0 ? (
                    <Table size="small" sx={tableHeadSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Due on</TableCell>
                          <TableCell>Amount paid</TableCell>
                          <TableCell>Paid on</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedInstallments.map((installment, index) => {
                          const isPaid =
                            String(installment.status || "").toUpperCase() ===
                            "PAID";
                          return (
                            <TableRow key={installment._id || index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                {formatDate(installment.installmentDate)}
                              </TableCell>
                              <TableCell>
                                {isPaid
                                  ? formatAmount(
                                      installment.totalPaidAmount ??
                                        installment.paidAmount
                                    )
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {isPaid
                                  ? formatDate(installment.updatedAt)
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={installment.status || "N.A"}
                                  color={statusColor(installment.status)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ color: "#6B6780" }}>
                      {mandateStillPending
                        ? "No installments yet. This savings plan starts after the customer approves auto-debit and the first payment succeeds."
                        : "No installments recorded for this savings plan."}
                    </Typography>
                  )}
                </SectionCard>
              )}

              <SectionCard
                icon={<ReceiptLongIcon />}
                title="Bank debit history"
                subtitle="When auto-debit was requested and when money actually came from the bank"
              >
                {sortedCharges.length > 0 ? (
                  <TableContainer
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        ...tableHeadSx,
                        minWidth: 980,
                        "& td": { whiteSpace: "nowrap" },
                      }}
                    >
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Requested on</TableCell>
                        <TableCell>Scheduled for</TableCell>
                        <TableCell>Money deducted on</TableCell>
                        <TableCell>Result</TableCell>
                        <TableCell>Why it failed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCharges.map((charge, index) => (
                        <TableRow
                          key={charge._id || charge.charge_id || index}
                        >
                          <TableCell>{charge.charge_id || "N.A"}</TableCell>
                          <TableCell>{formatAmount(charge.amount)}</TableCell>
                          <TableCell>
                            {formatDateTime(charge.createdAt)}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(charge.schedule_date)}
                          </TableCell>
                          <TableCell>
                            {charge.status === "Success"
                              ? formatDateTime(
                                  charge.updatedAt || charge.createdAt
                                )
                              : "Not yet"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={chargeStatusLabel(charge.status)}
                              color={statusColor(charge.status)}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 220,
                              maxWidth: 320,
                              whiteSpace: "normal !important",
                            }}
                          >
                            {String(charge.status).toUpperCase() === "FAILED"
                              ? charge.failureReason ||
                                selectedSubscription.mandateFailureReason ||
                                "Gateway did not send a reason"
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography sx={{ color: "#6B6780" }}>
                    No bank debit has been requested yet. After the first
                    payment, each auto-debit will appear here.
                  </Typography>
                )}
              </SectionCard>
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                py: 2,
                backgroundColor: "#fff",
                borderTop: "1px solid #EDEAF5",
              }}
            >
              {canCancelRow(selectedSubscription) && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => setCancelTarget(selectedSubscription)}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  Stop auto-debit
                </Button>
              )}
              <Button
                onClick={() => setSelectedSubscription(null)}
                sx={{ textTransform: "none", borderRadius: "10px" }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => !cancelling && setCancelTarget(null)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Stop auto-debit?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#4A4460" }}>
            This cancels the customer's UPI mandate. The bank will not deduct
            future installments automatically. Their SIP stays active, so they
            can still pay manually.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disabled={cancelling}
            onClick={() => setCancelTarget(null)}
            sx={{ textTransform: "none" }}
          >
            Keep auto-debit
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelling}
            onClick={handleCancelConfirm}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            {cancelling ? "Stopping..." : "Stop auto-debit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllSubscriptionList;
